import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface ServiceAsset {
  id: string;
  sermon_id: string | null;
  songs: string[];
  hymns: string[];
  readings: string[];
  archived: boolean;
  created_at: string;
}

interface CreateServiceAssetData {
  sermon_id?: string;
  songs?: string[];
  hymns?: string[];
  readings?: string[];
}

export function useServiceAssets() {
  const [assets, setAssets] = useState<ServiceAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const loadAssets = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error: fetchError } = await supabase
        .from('service_assets')
        .select('*')
        .eq('user_id', user.id)
        .eq('archived', showArchived)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      setAssets(data || []);
    } catch (err) {
      console.error('Error loading service assets:', err);
      setError(err instanceof Error ? err.message : 'Failed to load service assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, [showArchived]);

  const saveAsset = async (data: CreateServiceAssetData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: savedAsset, error: saveError } = await supabase
        .from('service_assets')
        .insert([{
          ...data,
          user_id: user.id
        }])
        .select()
        .single();

      if (saveError) throw saveError;
      await loadAssets();
      return savedAsset;
    } catch (err) {
      console.error('Error saving service asset:', err);
      throw err;
    }
  };

  const toggleArchive = async (id: string, currentState: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: updateError } = await supabase
        .from('service_assets')
        .update({ archived: !currentState })
        .eq('id', id)
        .eq('user_id', user.id);

      if (updateError) throw updateError;
      await loadAssets();
    } catch (err) {
      console.error('Error toggling archive:', err);
      throw err;
    }
  };

  const deleteAsset = async (id: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error: deleteError } = await supabase
        .from('service_assets')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;
      await loadAssets();
    } catch (err) {
      console.error('Error deleting service asset:', err);
      throw err;
    }
  };

  return {
    assets,
    loading,
    error,
    showArchived,
    setShowArchived,
    saveAsset,
    toggleArchive,
    deleteAsset,
    refresh: loadAssets
  };
}